import { showToast } from "./toast.service";

/**
 * Plain-language API error layer, shared byte-for-byte by finex-web and inventory-web.
 *
 * The APIs answer every failure as { statusCode, code, message, errors?[{ property, code,
 * params, messages }] }: `message` is already a plain English sentence naming the first
 * problem, and each field error carries the rule that failed (maxLength, max, isNotEmpty...)
 * with its argument. This file turns that (and the older shapes still in use) into a toast
 * plus per-field messages, translated through the host's `language` map by code, never by
 * guessing from the English text. Copy keys live only in the host's public/locales.
 */

type Lang = Record<string, string> | null | undefined;

const t = (language: Lang, key: string, fallback: string): string =>
  language && language[key] ? language[key] : fallback;

const fill = (text: string, params: Record<string, unknown> = {}) =>
  text.replace(/\{(\w+)\}/g, (match, name) => (params[name] === undefined ? match : String(params[name])));

const escapeHtml = (text: string) =>
  String(text).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] as string);

export interface ApiFieldError {
  code: string;
  params: Record<string, unknown>;
  /** The server's English text for this field. */
  message: string;
}

export interface NormalizedApiError {
  status: number;
  code: string;
  /** Server text, always a string. */
  message: string;
  fieldErrors: Record<string, ApiFieldError>;
  technical?: string;
  /** The request never got an answer (offline, CORS, timeout). */
  network: boolean;
}

export interface FriendlyError {
  /** Short headline for the toast. */
  title: string;
  /** Plain-language explanation + next step. */
  message: string;
  /** "POST /api/clients 400" — muted, for support/diagnosis. */
  technical?: string;
  /** Translated, per-field text, keyed by the form's field name. */
  fieldErrors?: Record<string, string>;
}

export interface ApiErrorContext {
  /** The component's `language` map (window.languageContext.language). */
  language?: Lang;
  /** Headline used when nothing more specific matches. */
  titleFallback?: string;
  /** Called with mapped field errors so the form can show them under each input. */
  setFieldErrors?: (errors: Record<string, string>) => void;
  /** Server property -> form field, where the names differ. */
  fieldAliases?: Record<string, string>;
  /** Multi-step forms: field -> step, so the form can jump to the earliest step in error. */
  fieldSteps?: Record<string, number>;
  onStep?: (step: number) => void;
  /** The caller's active locale ("en", "al"); the stored language is used when absent. */
  locale?: string | null;
}

const LEGACY_DB_MESSAGE = "Database error occurred";

/** Reads every error body shape the APIs and the core proxy send today. */
export function normalizeApiError(error: any): NormalizedApiError {
  const response = error?.response;
  const data = response?.data && typeof response.data === "object" ? response.data : {};
  const status: number = response?.status ?? 0;
  const method = (error?.config?.method ?? "").toUpperCase();
  const url = error?.config?.url ?? "";
  const technical = url ? `${method ? method + " " : ""}${url}${status ? ` ${status}` : ""}` : undefined;

  const rawMessage = data.message ?? (typeof response?.data === "string" ? response.data : undefined);
  const message = Array.isArray(rawMessage)
    ? rawMessage.filter(Boolean).join(" ")
    : typeof rawMessage === "string"
      ? rawMessage
      : typeof data.error === "string"
        ? data.error
        : "";

  const fieldErrors: Record<string, ApiFieldError> = {};
  const list = Array.isArray(data.errors) ? data.errors : Array.isArray(data.data?.errors) ? data.data.errors : [];
  for (const entry of list) {
    if (!entry || typeof entry !== "object" || !entry.property) continue;
    const messages: string[] = Array.isArray(entry.messages) ? entry.messages.filter(Boolean) : [];
    if (fieldErrors[entry.property]) continue;
    fieldErrors[entry.property] = {
      code: entry.code ?? "invalid",
      params: entry.params ?? {},
      message: messages[0] ?? "",
    };
  }

  return {
    status,
    code: data.code ?? (status ? `HTTP_${status}` : "NETWORK"),
    message,
    fieldErrors,
    technical,
    network: !response,
  };
}

/** Text shown under one input: translated from the rule that failed, server text as fallback. */
export function fieldErrorText(field: ApiFieldError, language?: Lang): string {
  const fallbacks: Record<string, string> = {
    maxLength: "Must be {max} characters or fewer.",
    minLength: "Must be at least {min} characters.",
    length: "Must be between {min} and {max} characters.",
    max: "Must be {max} or less.",
    min: "Must be {min} or more.",
    isNotEmpty: "This field is required.",
    isDefined: "This field is required.",
    isEmail: "Enter a valid email address.",
    isNumber: "Enter a number.",
    isInt: "Enter a whole number.",
    isUUID: "Choose an option.",
    isUuid: "Choose an option.",
    isEnum: "Choose one of the options.",
    isIn: "Choose one of the options.",
    isDate: "Enter a valid date.",
    isDateString: "Enter a valid date.",
    unique: "This is already used. Enter a different value.",
    exists: "This no longer exists. Choose another.",
  };
  const fallback = fallbacks[field.code];
  if (!fallback) return field.message || t(language, "err_field_invalid", "Check this value.");
  return fill(t(language, `err_field_${field.code}`, fallback), field.params);
}

interface FriendlyCopy {
  title: string;
  message: string;
}

/** Domain rules for messages the APIs still send as text. Frozen: new cases use `code`. */
function matchRule(status: number, rawMessage: string, language: Lang): FriendlyCopy | null {
  const m = (rawMessage || "").toLowerCase();

  // Product is missing its income/expense accounting account (COA mapping).
  if (
    m.includes("income coa") ||
    m.includes("expense coa") ||
    m.includes("product_income_codes") ||
    m.includes("product_expense_codes") ||
    m.includes("charts_of_accounts")
  ) {
    return {
      title: t(language, "err_account_setup_title", "Product needs an account"),
      message: t(
        language,
        "err_account_setup_msg",
        "This product isn't set up for accounting yet. Open the product, choose its income or expense account, then try again.",
      ),
    };
  }

  // Product is missing its base unit of measure.
  if (m.includes("product uom") || m.includes("uom id") || m.includes("base uom") || (m.includes("uom") && m.includes("uuid"))) {
    return {
      title: t(language, "err_uom_title", "Product needs a unit of measure"),
      message: t(
        language,
        "err_uom_msg",
        "This product has no base unit of measure. Open the product and set one before selling or transferring it.",
      ),
    };
  }

  // The accounting period is closed/locked.
  if (m.includes("period") && (m.includes("closed") || m.includes("locked"))) {
    return {
      title: t(language, "err_period_title", "Accounting period is closed"),
      message: t(
        language,
        "err_period_msg",
        "This period is closed, so nothing new can be posted to it. Choose an open accounting period and try again.",
      ),
    };
  }

  if (status === 401) {
    return {
      title: t(language, "err_session_title", "Your session expired"),
      message: t(language, "err_session_msg", "Please sign in again to continue."),
    };
  }
  if (status === 403) {
    return {
      title: t(language, "err_forbidden_title", "You don't have permission"),
      message: t(language, "err_forbidden_msg", "You don't have access to do this. Ask an administrator if you think this is a mistake."),
    };
  }
  if (status >= 500) {
    return {
      title: t(language, "err_server_title", "Something went wrong"),
      message: t(
        language,
        "err_server_msg",
        "This is a problem on our side, not yours. Please try again in a moment. If it keeps happening, contact support.",
      ),
    };
  }
  return null;
}

/** Copy for API codes that are about the record, not one field. */
function codeCopy(code: string, language: Lang): FriendlyCopy | null {
  switch (code) {
    case "DUPLICATE":
      return {
        title: t(language, "err_duplicate_title", "This already exists"),
        message: t(language, "err_duplicate_msg", "Something with the same details already exists. Change it and try again."),
      };
    case "IN_USE":
      return {
        title: t(language, "err_in_use_title", "This can't be removed"),
        message: t(language, "err_in_use_msg", "It is still used elsewhere. Remove it from there first, then try again."),
      };
    case "REFERENCE_NOT_FOUND":
      return {
        title: t(language, "err_reference_title", "Something it refers to is missing"),
        message: t(language, "err_reference_msg", "A selected item no longer exists. Refresh the page, choose again and save."),
      };
    case "VALUE_TOO_LONG":
      return {
        title: t(language, "err_too_long_title", "A value is too long"),
        message: t(language, "err_too_long_msg", "One of the values is longer than allowed. Shorten it and try again."),
      };
    case "NOT_FOUND":
    case "HTTP_404":
      return {
        title: t(language, "err_notfound_title", "Not found"),
        message: t(language, "err_notfound_msg", "We couldn't find what you were looking for. It may have been removed or renamed."),
      };
    case "CONFLICT":
    case "HTTP_409":
      return {
        title: t(language, "err_conflict_title", "This conflicts with existing data"),
        message: t(language, "err_conflict_msg", "This clashes with something that already exists. Refresh the page and try again."),
      };
    case "UPSTREAM_UNAVAILABLE":
      return {
        title: t(language, "err_network_title", "Can't reach the server"),
        message: t(language, "err_network_msg", "Check your internet connection and try again."),
      };
    default:
      return null;
  }
}

const isEnglish = (locale?: string | null) => {
  try {
    return (locale || localStorage.getItem("language") || "en").toLowerCase().startsWith("en");
  } catch {
    return true;
  }
};

export function describeApiError(error: any, ctx: ApiErrorContext = {}): FriendlyError {
  const { language } = ctx;
  const api = normalizeApiError(error);
  const technical = api.technical;

  const fieldErrors: Record<string, string> = {};
  for (const [property, field] of Object.entries(api.fieldErrors)) {
    fieldErrors[ctx.fieldAliases?.[property] ?? property] = fieldErrorText(field, language);
  }
  const hasFields = Object.keys(fieldErrors).length > 0;
  const serverText = api.message === LEGACY_DB_MESSAGE ? "" : api.message;

  if (api.network) {
    return {
      title: t(language, "err_network_title", "Can't reach the server"),
      message: t(language, "err_network_msg", "Check your internet connection and try again."),
      technical,
    };
  }

  const matched = matchRule(api.status, serverText, language);
  if (matched) return { ...matched, technical, fieldErrors: hasFields ? fieldErrors : undefined };

  // English users get the server's own sentence ("Company name must be 100 characters or
  // fewer."); other languages get the translated summary, with the fields marked on the form.
  const byCode = codeCopy(api.code, language);
  if (hasFields || api.code === "VALIDATION_FAILED") {
    const title = byCode?.title ?? ctx.titleFallback ?? t(language, "err_check_form_title", "Please check the form");
    const generic = byCode?.message ?? t(language, "err_check_form_msg", "Some fields need attention. The highlighted fields show what to fix.");
    return {
      title,
      message: isEnglish(ctx.locale) && serverText ? serverText : generic,
      technical,
      fieldErrors: hasFields ? fieldErrors : undefined,
    };
  }
  if (byCode) {
    return { ...byCode, message: isEnglish(ctx.locale) && serverText && api.code !== "UPSTREAM_UNAVAILABLE" ? serverText : byCode.message, technical };
  }

  return {
    title: ctx.titleFallback ?? t(language, "err_generic_title", "Something didn't work"),
    message: serverText || t(language, "err_generic_msg", "Please try again. If it keeps happening, contact support."),
    technical,
  };
}

/**
 * Show one error toast, hand per-field messages to the form and, for multi-step forms,
 * jump to the earliest step holding a field in error. Returns what was shown.
 */
export function showApiError(error: any, ctx: ApiErrorContext = {}): FriendlyError {
  const fe = describeApiError(error, ctx);
  const fields = Object.keys(fe.fieldErrors ?? {});
  if (fields.length > 0) {
    ctx.setFieldErrors?.(fe.fieldErrors as Record<string, string>);
    const steps = fields.map((field) => ctx.fieldSteps?.[field]).filter((value): value is number => value !== undefined);
    if (steps.length > 0) ctx.onStep?.(Math.min(...steps));
  }
  const techHtml = fe.technical ? `<br/><span style='color:#9AA8A8;font-size:11px'>${escapeHtml(fe.technical)}</span>` : "";
  showToast({
    message: fe.title,
    desc: `<p class='font-inter text-[14px] text-main leading-6'>${escapeHtml(fe.message)}${techHtml}</p>`,
    type: "error",
    duration: 20000,
  });
  return fe;
}
